import type { Membership } from '../api/client';

/**
 * An end user's own portal: their phone (only with an extension: My phone, its voicemail and their
 * calls), Messages, and whatever their company's role adds. Their own settings and Help are in the
 * menu for everyone. Messaging-only people (no extension, no role) get Messages, and land there.
 */
export type UserPage = 'me' | 'console' | 'messages' | 'meetings' | 'calls' | 'wallboard' | 'reports' | 'billing';

export function userPages(m: (Pick<Membership, 'extension' | 'permissions'> & Partial<Pick<Membership, 'seat' | 'role' | 'roleId'>>) | undefined): UserPage[] {
  const perms = m?.permissions ?? {};
  // A role of only Billing and no phone (a bookkeeper, 1.13.0): Billing, and nothing else.
  if (m && !m.extension && Object.keys(perms).length === 1 && perms.billing) return ['billing'];
  const pages: UserPage[] = [];
  if (m?.extension) pages.push('me');
  // The receptionist seat (1.13.0): the Console.
  if (m?.extension && m.seat === 'receptionist') pages.push('console');
  pages.push('messages');
  // Meetings (1.13.0): everyone in the company.
  pages.push('meetings');
  if (perms.calls) pages.push('calls');
  if (perms.wallboard || perms.agents) pages.push('wallboard');
  if (perms.reports) pages.push('reports');
  if (perms.billing) pages.push('billing');
  return pages;
}

/**
 * Whether Billing shows (1.13.0): to Stitch staff always (to look with Open their portal), and to the
 * company's own people once staff turn "Customers see Billing" on (`billingVisible`). Until then their
 * menus have no Billing, and the page itself says it starts soon (so a bookkeeper's app isn't empty).
 */
export function billingShown(staff: boolean | undefined, m: Pick<Membership, 'tenant' | 'billingVisible'> | undefined): boolean {
  return Boolean(staff) || m?.billingVisible === true;
}

/** Messaging only (no phone): an end user with no extension and no role of the company's. */
export function isMessagingOnly(m: Pick<Membership, 'role' | 'extension' | 'roleId'> | undefined): boolean {
  return Boolean(m && m.role === 'user' && !m.extension && !m.roleId);
}

/** Phone-size screens get the phone app (components/PhoneApp), whose Home is Messages. */
export const PHONE_QUERY = '(max-width: 860px)';
export function isPhoneScreen(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;
}

/** Pages of the phone app every company member has: its DMs, Activity and More tabs. */
export const PHONE_APP_PAGES = ['dms', 'feed', 'more'] as const;

/**
 * Where someone lands in a company: its admin home, their own phone, or (no extension) Messages.
 * On a phone, the phone app's Home (Messages) for everyone.
 */
export function landingPath(m: Pick<Membership, 'tenant' | 'role' | 'extension' | 'permissions'> & Partial<Pick<Membership, 'seat'>>, phone = isPhoneScreen()): string {
  const base = `/t/${encodeURIComponent(m.tenant)}`;
  if (phone) return `${base}/messages`;
  // A receptionist (1.13.0) starts at their console.
  if (m.role === 'user' && m.extension && m.seat === 'receptionist') return `${base}/console`;
  return m.role === 'user' ? `${base}/${userPages(m)[0]}` : base;
}
